import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, putU32, readU16, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16, MAX_U32 } from "./limits.ts";

/**
 * The two frames of harvested props (ADR-142 decision 3.5, PROTOCOL.md 5.22 and 5.23), both server to client, both
 * binary and both written into buffers made once and decoded into records made once: a harvest is a crowd's event,
 * and neither frame allocates, throws on hostile bytes or reads past its end (ADR-030).
 *
 * `harvested` is the list of one chunk's harvested props, sent when the chunk enters a page's watch: after the header,
 * a `u16` chunk column, a `u16` chunk row, a `u16` count of 0 to `MAX_HARVEST_LIST`, then a `u16` prop index each. An
 * empty list is a frame like any other; it tells the page the chunk holds nothing harvested, which replaces whatever
 * the page held for it. `harvestEvent` is a change inside a watched chunk: after the header, a `u32` tick, a `u8` count
 * of 1 to `MAX_HARVEST_EVENTS`, then 3 bytes an event, a `u16` prop index and a `u8` code. A prop's index is its place
 * among its map's harvestable props sorted by authored id (`harvestPropOrder`).
 */

/**
 * Bytes before the first index of a `harvested` frame: the header, the chunk column and row, and the count.
 */
export const HARVEST_LIST_HEADER_BYTES = HEADER_BYTES + 6;

/**
 * The most indices one `harvested` frame lists: a chunk holds no more harvestable props than this, and the compile
 * refuses a map that does.
 */
export const MAX_HARVEST_LIST = 1024;

/**
 * Bytes before the first event of a `harvestEvent` frame: the header, the tick and the count.
 */
export const HARVEST_EVENT_HEADER_BYTES = HEADER_BYTES + 5;

/**
 * Bytes of one event: the prop's index and the code.
 */
export const HARVEST_EVENT_ENTRY_BYTES = 3;

/**
 * The most events one `harvestEvent` frame carries.
 */
export const MAX_HARVEST_EVENTS = 16;

/**
 * The event codes this build knows. A page skips a code it does not know, so a later server may add one.
 */
export const HarvestEventCode = { Harvested: 1, Regrown: 2 } as const;
export type HarvestEventCode = (typeof HarvestEventCode)[keyof typeof HarvestEventCode];

const LIST_COUNT_OFFSET = HEADER_BYTES + 4;
const EVENT_COUNT_OFFSET = HEADER_BYTES + 4;
const MAX_BYTE = 255;

/**
 * A decoded `harvested` frame, reused across frames: the chunk, how many indices the frame held, and the indices, the
 * first `count` valid.
 */
export interface HarvestListRecord {
  cx: number;
  cy: number;
  count: number;
  readonly indices: Uint16Array;
}

/**
 * A fresh record for `decodeHarvestList` to fill, with room for the most a frame lists.
 *
 * @returns An empty record.
 */
export function newHarvestListRecord(): HarvestListRecord {
  return { cx: 0, cy: 0, count: 0, indices: new Uint16Array(MAX_HARVEST_LIST) };
}

/**
 * Bytes of a `harvested` frame of a number of indices.
 *
 * @param count The indices.
 * @returns `9 + 2 * count`.
 */
export function harvestListBytes(count: number): number {
  return HARVEST_LIST_HEADER_BYTES + 2 * count;
}

/**
 * Writes one index at its place in a `harvested` frame through plain byte stores. The caller has sized the buffer and
 * checked the index's width.
 *
 * @param buffer The frame.
 * @param place The index's place in the list, from 0.
 * @param index The prop's index.
 */
export function putHarvestListIndex(buffer: Uint8Array, place: number, index: number): void {
  putU16(buffer, HARVEST_LIST_HEADER_BYTES + 2 * place, index);
}

/**
 * Writes a `harvested` frame's header once its indices are in place.
 *
 * @param buffer The frame.
 * @param cx The chunk's column.
 * @param cy The chunk's row.
 * @param count The indices written, 0 to `MAX_HARVEST_LIST`.
 * @returns The frame's length in bytes.
 */
export function putHarvestListHeader(buffer: Uint8Array, cx: number, cy: number, count: number): number {
  putHeader(buffer, MessageType.Harvested);
  putU16(buffer, HEADER_BYTES, cx);
  putU16(buffer, HEADER_BYTES + 2, cy);
  putU16(buffer, LIST_COUNT_OFFSET, count);
  return harvestListBytes(count);
}

/**
 * Encodes a `harvested` frame into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `harvestListBytes(indices.length)` long.
 * @param cx The chunk's column.
 * @param cy The chunk's row.
 * @param indices 0 to 1024 prop indices.
 * @returns Bytes written.
 * @throws Error when there are more than 1024 indices, the buffer is too small, or a field does not fit its width.
 */
export function encodeHarvestList(buffer: Uint8Array, cx: number, cy: number, indices: readonly number[]): number {
  if (indices.length > MAX_HARVEST_LIST) {
    throw new Error(`harvested lists 0 to ${MAX_HARVEST_LIST} indices, not ${indices.length}`);
  }
  const length = harvestListBytes(indices.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte harvested`);
  }
  assertField(cx, MAX_U16, `harvested cx ${cx}`);
  assertField(cy, MAX_U16, `harvested cy ${cy}`);
  for (let place = 0; place < indices.length; place += 1) {
    const index = indices[place] as number;
    assertField(index, MAX_U16, `harvested index ${index}`);
    putHarvestListIndex(buffer, place, index);
  }
  return putHarvestListHeader(buffer, cx, cy, indices.length);
}

/**
 * Decodes a `harvested` frame into a record. Never throws and reads no byte past the frame: a frame shorter than its
 * header, a count past `MAX_HARVEST_LIST`, a length other than `9 + 2 * count` or another type is malformed, and
 * another version a mismatch, each leaving the record untouched. The length is compared before an index is read, so a
 * hostile count sizes nothing.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodeHarvestList(frame: Uint8Array, out: HarvestListRecord): DecodeVerdict {
  if (frame.length < HARVEST_LIST_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = readU16(frame, LIST_COUNT_OFFSET);
  if (count > MAX_HARVEST_LIST) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.Harvested, harvestListBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  for (let place = 0; place < count; place += 1) {
    out.indices[place] = readU16(frame, HARVEST_LIST_HEADER_BYTES + 2 * place);
  }
  out.cx = readU16(frame, HEADER_BYTES);
  out.cy = readU16(frame, HEADER_BYTES + 2);
  out.count = count;
  return OK;
}

/**
 * One event to encode: the prop's index and the event code.
 */
export interface HarvestEventEntry {
  readonly index: number;
  readonly code: number;
}

/**
 * A decoded `harvestEvent`, reused across frames: the tick, how many events the frame held, and their indices and
 * codes, the first `count` of each valid.
 */
export interface HarvestEventRecord {
  tick: number;
  count: number;
  readonly indices: Uint16Array;
  readonly codes: Uint8Array;
}

/**
 * A fresh record for `decodeHarvestEvent` to fill, with room for the most events a frame carries.
 *
 * @returns An empty record.
 */
export function newHarvestEventRecord(): HarvestEventRecord {
  return { tick: 0, count: 0, indices: new Uint16Array(MAX_HARVEST_EVENTS), codes: new Uint8Array(MAX_HARVEST_EVENTS) };
}

/**
 * Bytes of a `harvestEvent` frame of a number of events.
 *
 * @param count The events.
 * @returns `8 + 3 * count`.
 */
export function harvestEventBytes(count: number): number {
  return HARVEST_EVENT_HEADER_BYTES + HARVEST_EVENT_ENTRY_BYTES * count;
}

/**
 * Writes one event at its place in a frame through plain byte stores. The caller has sized the buffer and checked the
 * fields' widths.
 *
 * @param buffer The frame.
 * @param place The event's place, from 0.
 * @param index The prop's index.
 * @param code The event code.
 */
export function putHarvestEventEntry(buffer: Uint8Array, place: number, index: number, code: number): void {
  const at = HARVEST_EVENT_HEADER_BYTES + HARVEST_EVENT_ENTRY_BYTES * place;
  putU16(buffer, at, index);
  buffer[at + 2] = code;
}

/**
 * Writes a frame's header once its events are in place.
 *
 * @param buffer The frame.
 * @param tick The server tick, a `u32`.
 * @param count The events written, 1 to 16.
 * @returns The frame's length in bytes.
 */
export function putHarvestEventHeader(buffer: Uint8Array, tick: number, count: number): number {
  putHeader(buffer, MessageType.HarvestEvent);
  putU32(buffer, HEADER_BYTES, tick);
  buffer[EVENT_COUNT_OFFSET] = count;
  return harvestEventBytes(count);
}

/**
 * Encodes a `harvestEvent` into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `harvestEventBytes(events.length)` long.
 * @param tick The server tick.
 * @param events 1 to 16 events.
 * @returns Bytes written.
 * @throws Error when there are no events or more than 16, the buffer is too small, or a field does not fit its width.
 */
export function encodeHarvestEvent(buffer: Uint8Array, tick: number, events: readonly HarvestEventEntry[]): number {
  if (events.length < 1 || events.length > MAX_HARVEST_EVENTS) {
    throw new Error(`harvestEvent carries 1 to ${MAX_HARVEST_EVENTS} events, not ${events.length}`);
  }
  const length = harvestEventBytes(events.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte harvestEvent`);
  }
  assertField(tick, MAX_U32, `harvestEvent tick ${tick}`);
  for (let place = 0; place < events.length; place += 1) {
    const entry = events[place] as HarvestEventEntry;
    assertField(entry.index, MAX_U16, `harvestEvent index ${entry.index}`);
    assertField(entry.code, MAX_BYTE, `harvestEvent code ${entry.code}`);
    putHarvestEventEntry(buffer, place, entry.index, entry.code);
  }
  return putHarvestEventHeader(buffer, tick, events.length);
}

/**
 * Decodes a `harvestEvent` frame into a record. Never throws and reads no byte past the frame: a frame shorter than
 * its header, a count of 0 or past 16, a length other than `8 + 3 * count` or another type is malformed, and another
 * version a mismatch, each leaving the record untouched. A code this build does not know is decoded as it is, for the
 * page to skip.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodeHarvestEvent(frame: Uint8Array, out: HarvestEventRecord): DecodeVerdict {
  if (frame.length < HARVEST_EVENT_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[EVENT_COUNT_OFFSET] as number;
  if (count < 1 || count > MAX_HARVEST_EVENTS) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.HarvestEvent, harvestEventBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  for (let place = 0; place < count; place += 1) {
    const at = HARVEST_EVENT_HEADER_BYTES + HARVEST_EVENT_ENTRY_BYTES * place;
    out.indices[place] = readU16(frame, at);
    out.codes[place] = frame[at + 2] as number;
  }
  out.tick = readU32(frame, HEADER_BYTES);
  out.count = count;
  return OK;
}
